import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { listPlans, listTenants, savePlan, deletePlan } from "../../../../services/platform_admin_service";
import { FEATURE_KEYS } from "../constants";
import { PLAN_FEATURES } from "../../../../constants/plans";

export const mapEffectiveToCommercialFeatures = (effectiveFeatures = {}, basePlanId = "neo") => {
    const features = {};
    const defaults = PLAN_FEATURES[basePlanId] || {};
    for (const key of FEATURE_KEYS) {
        features[key] = effectiveFeatures[key] !== undefined ? !!effectiveFeatures[key] : !!defaults[key];
    }
    return features;
};

export const mapCommercialToTechnicalOverrides = (commercialFeatures = {}) => {
    const overrides = {};
    for (const key of FEATURE_KEYS) {
        if (commercialFeatures[key] !== undefined) {
            overrides[key] = !!commercialFeatures[key];
        }
    }
    return overrides;
};

const slugify = (text) => {
    return text
        .toString()
        .toLowerCase()
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
};

export const mapBackendToFrontendPlan = (plan) => ({
    id: plan.id,
    code: plan.code || plan.id,
    name: plan.title || plan.label || plan.code || "",
    monthlyPrice: plan.price || 0,
    annualPrice: plan.annualPrice || 0,
    studentLimit: plan.studentLimit !== undefined ? plan.studentLimit : -1,
    userLimit: plan.userLimit !== undefined ? plan.userLimit : -1,
    basePlanId: plan.basePlanId || "neo",
    active: plan.active !== false,
    features: mapEffectiveToCommercialFeatures(plan.effectiveFeatures || {}, plan.basePlanId || "neo"),
    source: plan.source,
    methodologyNeoPrice: plan.methodologyNeoPrice !== undefined ? plan.methodologyNeoPrice : 149,
});

export const mapFrontendToBackendPlan = (plan) => {
    const code = plan.code || slugify(plan.name || "");
    return {
        id: code,
        code: code,
        title: plan.name || "",
        price: plan.monthlyPrice || 0,
        annualPrice: plan.annualPrice || 0,
        studentLimit: plan.studentLimit !== undefined ? plan.studentLimit : -1,
        userLimit: plan.userLimit !== undefined ? plan.userLimit : -1,
        basePlanId: plan.basePlanId || "basic",
        active: plan.active !== false,
        featureOverrides: mapCommercialToTechnicalOverrides(plan.features || {}),
        methodologyNeoPrice: plan.methodologyNeoPrice !== undefined ? plan.methodologyNeoPrice : 149,
    };
};

const usePlatformPlans = () => {
    const [plans, setPlans] = useState([]);
    const [basePlanIds, setBasePlanIds] = useState([]);
    const [planVersion, setPlanVersion] = useState(null);
    const [tenantsByPlan, setTenantsByPlan] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [plansResult, tenantsResult] = await Promise.all([
                listPlans(),
                listTenants({ page: 1, pageSize: 100 }),
            ]);

            const grouped = {};
            for (const t of (tenantsResult.items || [])) {
                const plan = t.platformPlanId || t.planId || "unknown";
                if (!grouped[plan]) grouped[plan] = [];
                grouped[plan].push(t);
            }

            const mappedPlans = (plansResult.items || []).map(mapBackendToFrontendPlan);
            setPlans(mappedPlans);
            setBasePlanIds(plansResult.basePlanIds || []);
            setPlanVersion(plansResult.planVersion || null);
            setTenantsByPlan(grouped);
        } catch (err) {
            setError(err.message || "Erro ao carregar planos.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const saveCommercialPlan = async (plan) => {
        try {
            setLoading(true);
            setError(null);
            const backendPlan = mapFrontendToBackendPlan(plan);
            const result = await savePlan(backendPlan);
            await fetchData();
            toast.success("Plano salvo com sucesso!");
            return result;
        } catch (err) {
            const msg = err.message || "Erro ao salvar plano.";
            setError(msg);
            toast.error(msg);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const deleteCommercialPlan = async (planId) => {
        try {
            setLoading(true);
            setError(null);
            const result = await deletePlan(planId);
            await fetchData();
            toast.success("Plano excluído com sucesso!");
            return result;
        } catch (err) {
            const msg = err.message || "Erro ao excluir plano.";
            setError(msg);
            toast.error(msg);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return {
        plans,
        basePlanIds,
        planVersion,
        tenantsByPlan,
        loading,
        error,
        saveCommercialPlan,
        deleteCommercialPlan,
        refetch: fetchData,
    };
};

export default usePlatformPlans;
